import LockIcon from '@mui/icons-material/Lock';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { getCountryName } from 'constants/countries';
import type React from 'react';
import { useState } from 'react';
import { useGetClientScoringQuery } from 'services/api/clients.api';
import priceFormat from 'services/format/priceFormat';
import type { BusinessUnit } from 'services/types/businessUnit';
import type { Client } from 'services/types/client';
import type { Lead } from 'services/types/lead';
import type { User } from 'services/types/user';
import { NAVY, RADIUS_COIN, RADIUS_ITEM, SHADOW, SURFACE_CONTAINER_LOWEST, YELLOW } from 'styles/tokens';
import { formatDOB } from 'utils/formatDOB';

import CallManager from '../CallManager';
import UserAssigner from '../UserAssigner';
import BankOffersSection from './BankOffersSection';
import CarInsuranceQuotesSection from './CarInsuranceQuotesSection';
import ClientCarsSection from './ClientCarsSection';
import ClientNotesEditor from './ClientNotesEditor';
import DocumentsSection from './DocumentsSection';
import InfoItem from './InfoItem';
import LeadsSection from './LeadsSection';
import SalesSection from './SalesSection';
import SimulatorSection from './SimulatorSection';
import SummaryBox from './SummaryBox';

interface AutoredData {
  vin?: string;
  year?: number;
  color?: string;
  fuelType?: string | null;
  vehicleType?: string;
  showName?: string;
  versionName?: string;
  engineNumber?: string;
  licensePlate?: string;
}

/**
 * Helper function to create InfoItem components
 * @param label - The label for the info item
 * @param value - The value to display
 * @param fullWidth - Whether to take full width
 * @returns InfoItem component
 */
const createInfoItem = (label: string, value: React.ReactNode | undefined | null, fullWidth = false) => {
  // Always display the item, show "-" if value is null, undefined, or empty string
  const displayValue = value === null || value === undefined || value === '' ? '-' : value;
  return <InfoItem key={label} label={label} value={displayValue} fullWidth={fullWidth} />;
};

const createChatSummaryBox = (chatSummary: unknown) => {
  if (typeof chatSummary !== 'string' || chatSummary.trim() === '') return null;
  return <SummaryBox text={chatSummary} />;
};

interface LegacyAddress {
  street?: string | null;
  number?: string | null;
  homeNumber?: string | null;
  commune?: string | null;
  region?: string | null;
  regionId?: string | null;
}

/**
 * Formats an address object into a readable string
 * @param address - Address object with street, number, homeNumber, commune, region or a string
 * @returns Formatted address string
 */
const formatAddress = (address: LegacyAddress | string | null | undefined): string => {
  if (!address) return '';

  // If it's already a string, return it as is
  if (typeof address === 'string') return address;

  // TypeScript now knows this is an Address object
  const parts: string[] = [];

  // Add street and number
  if (address.street) {
    let streetPart = address.street;
    if (address.number) {
      streetPart += ` ${address.number}`;
    }
    parts.push(streetPart);
  }

  // Add home number (apartment/unit)
  if (address.homeNumber) {
    parts.push(`Depto. ${address.homeNumber}`);
  }

  // Add commune
  if (address.commune) {
    parts.push(address.commune);
  }

  // Add region
  if (address.region) {
    parts.push(address.region);
  }

  return parts.join(', ');
};

export const renderClientNotes = (client: Client) => <ClientNotesEditor client={client} />;

/**
 * Renders personal client information section (Información Personal)
 * Contains: Nationality, Sex, Address, Bank Account
 */
export const renderPersonalInfo: (
  client: Client,
  businessUnit?: BusinessUnit,
  currentUser?: User | null,
  clientLeads?: Lead[]
) => React.ReactNode = (client) => {
  const bankAccount = client.bankAccount as
    | {
        bankName?: string | null;
        accountType?: 'VISTA' | 'CORRIENTE' | 'RUT' | null;
        accountNumber?: string | null;
      }
    | null
    | undefined;

  const accountTypeLabels: Record<string, string> = {
    VISTA: 'Cuenta Vista',
    CORRIENTE: 'Cuenta Corriente',
    RUT: 'Cuenta RUT',
  };
  let accountType = null;
  if (bankAccount?.accountType && accountTypeLabels[bankAccount.accountType]) {
    accountType = bankAccount.accountType;
  }

  return (
    <Grid container spacing={0.5}>
      {createInfoItem('Fecha de Nacimiento', formatDOB(client.dateOfBirth))}
      {createInfoItem('Nacionalidad', getCountryName(client.nationality) ?? client.nationality)}
      {createInfoItem('Sexo', client.gender)}
      {createInfoItem('Dirección', client.address ? formatAddress(client.address) : null, true)}
      {createInfoItem('Banco', bankAccount?.bankName)}
      {createInfoItem('Tipo de cuenta', accountType)}
      {createInfoItem('Número de cuenta', bankAccount?.accountNumber)}
    </Grid>
  );
};

/**
 * Renders car dealership specific information
 * Contains: Marca de interés, Modelo de interés, ID CRM, Fecha visita, Sucursal, Requiere financiamiento
 */
export const renderCarDealerInfo: (
  client: Client,
  businessUnit?: BusinessUnit,
  currentUser?: User | null,
  clientLeads?: Lead[]
) => React.ReactNode = (client) => (
  <Grid container spacing={0.5}>
    {createInfoItem('Marca de interés', client.brandOfInterest)}
    {createInfoItem('Modelo de interés', client.modelsOfInterest?.join(', '))}
    {createInfoItem('ID CRM', client.externalId)}
    {createInfoItem('Sucursal', client.branchOffice)}
    {createInfoItem('Fecha de visita', client.visitDate ? new Date(client.visitDate).toLocaleString('es-CL') : null)}
    {createInfoItem('Financiamiento', client.requiresFinancing ? 'Sí' : 'No')}
  </Grid>
);

/**
 * Renders the client's vehicles as one Accordion per ClientCar (title = licensePlate).
 */
export const renderClientCars: (
  client: Client,
  businessUnit?: BusinessUnit,
  currentUser?: User | null,
  clientLeads?: Lead[]
) => React.ReactNode = (client) => <ClientCarsSection clientId={client.id} />;

/**
 * Renders visit / CRM tracking info: branch, external CRM id and visit date.
 */
export const renderVisitInfo: (
  client: Client,
  businessUnit?: BusinessUnit,
  currentUser?: User | null,
  clientLeads?: Lead[]
) => React.ReactNode = (client) => (
  <Grid container spacing={0.5}>
    {createInfoItem('Sucursal', client.branchOffice)}
    {createInfoItem('ID CRM', client.externalId)}
    {createInfoItem('Fecha de visita', client.visitDate ? new Date(client.visitDate).toLocaleString('es-CL') : null)}
  </Grid>
);

/**
 * Renders the list of client sales (one Accordion per Sale).
 */
export const renderSales = (client: Client) => (
  <SalesSection
    clientId={client.id}
    clientRut={client.rut}
    clientGender={client.gender}
    clientDateOfBirth={client.dateOfBirth}
    clientNationality={client.nationality}
    clientEmail={client.email}
    clientAddress={client.address}
  />
);

/**
 * Renders the Insurex simulator form.
 */
export const renderSimulator = (client: Client) => <SimulatorSection clientId={client.id} />;

/**
 * Renders vehicle (Autored) info and the car insurance quotes section.
 */
export const renderVehicleAndQuotes = (client: Client) => {
  const autoredData = client.customAttributes?.autoredData as AutoredData | undefined;

  return (
    <Box>
      {autoredData && (
        <>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: 'primary.main',
            }}
          >
            Información del Vehículo
          </Typography>
          <Grid container spacing={0.5} sx={{ mb: 1 }}>
            {createInfoItem('Patente', autoredData.licensePlate)}
            {createInfoItem('Nombre Completo', autoredData.showName)}
            {createInfoItem('Versión', autoredData.versionName)}
            {createInfoItem('Año', autoredData.year?.toString())}
            {createInfoItem('Tipo de Vehículo', autoredData.vehicleType)}
            {createInfoItem('Tipo de Combustible', autoredData.fuelType)}
            {createInfoItem('Color', autoredData.color)}
            {createInfoItem('VIN', autoredData.vin)}
            {createInfoItem('Número de Motor', autoredData.engineNumber)}
          </Grid>
        </>
      )}
      <Box sx={{ mt: autoredData ? 3 : 0 }}>
        <CarInsuranceQuotesSection clientId={client.id} />
      </Box>
    </Box>
  );
};

/**
 * Renders surveys section
 */
export const renderSurveys: (
  client: Client,
  businessUnit?: BusinessUnit,
  currentUser?: User | null,
  clientLeads?: Lead[]
) => React.ReactNode = (client) => {
  if (!client.surveys || client.surveys.length === 0) return null;

  const SURVEY_STATUS_LABEL: Record<'pending' | 'finished' | 'aborted', string> = {
    finished: 'Completada',
    aborted: 'Abortada',
    pending: 'Sin terminar',
  };
  const SURVEY_STATUS_COLOR: Record<'pending' | 'finished' | 'aborted', 'success' | 'error' | 'warning'> = {
    finished: 'success',
    aborted: 'error',
    pending: 'warning',
  };

  return (
    <Box>
      {client.surveys.map((survey) => (
        <Box
          key={survey.id || survey.name}
          sx={{
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
              }}
            >
              {survey.name}
            </Typography>
            <Chip label={SURVEY_STATUS_LABEL[survey.status]} size="small" color={SURVEY_STATUS_COLOR[survey.status]} />
          </Box>
          <Box
            sx={{
              pl: 2,
            }}
          >
            {(survey.results ?? []).map((result, index) => (
              <Typography key={`${survey.id}-${result.question}-${index + 1}`} variant="body2" sx={{ mb: 0.5 }}>
                <strong>{index + 1}.</strong> {result.question}: {result.response || '-'}
              </Typography>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

/**
 * Renders operational information section (Estado y Configuración)
 * Contains: Respuestas automáticas, Follow Ups, Mensaje Inicial, Tópicos, Razón de término, Política de privacidad aceptada
 */
export const renderOperationalInfo: (
  client: Client,
  businessUnit?: BusinessUnit,
  currentUser?: User | null,
  clientLeads?: Lead[]
) => React.ReactNode = (client) => {
  return (
    <Grid container spacing={0.5}>
      {createInfoItem('Respuestas automáticas', client.disabledAutomaticResponses ? 'Desactivadas' : 'Activadas')}
      {createInfoItem('Follow ups', client.disabledFollowUps ? 'Desactivados' : 'Activados')}
      {createInfoItem('Mensaje inicial', client.prospectMessage, true)}
      {createInfoItem('Razón de término', client.endReason, false)}
      {createInfoItem('Política de privacidad', client.privacyPolicyAccepted ? 'Aceptada' : 'No aceptada')}
      {createInfoItem('Notas', client.notes, false)}
      {client.chatSummary && createInfoItem('', createChatSummaryBox(client.chatSummary), true)}
    </Grid>
  );
};

/**
 * Renders leads section with full functionality
 */
export const renderLeads = (
  _client: Client,
  businessUnit?: BusinessUnit,
  currentUser?: User | null,
  clientLeads?: Lead[]
) => <LeadsSection businessUnit={businessUnit} clientLeads={clientLeads} currentUser={currentUser} />;

/**
 * Renders documents section with full functionality
 */
export const renderDocuments = (client: Client, _businessUnit?: BusinessUnit, currentUser?: User | null) => (
  <DocumentsSection clientId={client.id} currentUser={currentUser} businessUnitId={client.businessUnitId} />
);

/**
 * Renders administrative settings for admin users
 */
export const renderAdminSettings = (client: Client) => {
  return (
    <Grid container spacing={0.5}>
      {createInfoItem('Cliente de prueba', client.isTestClient ? 'Sí' : 'No')}
      {createInfoItem('Costo total', priceFormat.format(client.cost || 0))}
      {createInfoItem('Safety Status', client.aiSafetyStatus)}
      {createInfoItem('FollowUps Sent', client.followUpsSent?.toString() || '0')}
    </Grid>
  );
};

/**
 * Renders user assignment section with consistent styling
 */
export const renderUserAssignment = (client: Client) => {
  return (
    <Grid container spacing={0.5}>
      {createInfoItem(
        '',
        <Box sx={{ mt: 1 }}>
          <UserAssigner client={client} />
        </Box>,
        true
      )}
    </Grid>
  );
};

/**
 * Renders call manager section with full functionality
 */
export const renderCallManager = (
  client: Client,
  businessUnit?: BusinessUnit,
  _currentUser?: User | null,
  _clientLeads?: Lead[],
  isConversationActive?: boolean
) => {
  if (!businessUnit?.hasCallingEnabled || !businessUnit) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <CallManager client={client} isConversationActive={!!isConversationActive} />
    </Box>
  );
};

/**
 * Component for rendering scoring information section
 * Shows all business units and clients associated with the client's phone number
 * Only accessible to admins and requires confirmation before loading data
 */
const ScoringSection: React.FC<{ clientId: number }> = ({ clientId }) => {
  const [isConfirmed, setIsConfirmed] = useState(false);
  const {
    data: scoringData,
    isLoading,
    error,
    refetch,
  } = useGetClientScoringQuery(clientId.toString(), {
    skip: !isConfirmed,
  });

  const handleConfirm = () => {
    setIsConfirmed(true);
    refetch().catch(() => {});
  };

  if (!isConfirmed) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 2,
        }}
      >
        <Button
          variant="outlined"
          color="primary"
          onClick={handleConfirm}
          sx={{
            textTransform: 'none',
            px: 3,
            py: 1,
            borderRadius: RADIUS_COIN,
            backgroundColor: YELLOW,
            color: NAVY,
            fontWeight: 600,
            border: 'none',
            '&:hover': {
              backgroundColor: YELLOW,
              opacity: 0.9,
              border: 'none',
            },
          }}
          startIcon={<LockIcon sx={{ fontSize: 18 }} />}
        >
          Confirmar y Cargar Información
        </Button>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          p: 2,
        }}
      >
        <Typography variant="body2" color="error">
          Error al cargar la información de scoring. Por favor, intenta nuevamente.
        </Typography>
      </Box>
    );
  }

  if (!scoringData || scoringData.businessUnitCount === 0) {
    return (
      <Box
        sx={{
          p: 2,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          No se encontraron múltiples unidades de negocio para este teléfono.
        </Typography>
      </Box>
    );
  }

  const businessUnitTypeLabels: Record<string, string> = {
    car_dealership: 'Concesionario',
    financial_institution: 'Instituciones Financieras',
    insurance_broker: 'Corredor de Seguros',
    default: 'Por defecto',
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Resumen */}
        <Grid size={12}>
          <Box
            sx={{
              mb: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: 'primary.main',
              }}
            >
              Resumen
            </Typography>
            <Grid container spacing={1}>
              {createInfoItem('Teléfono', scoringData.phone)}
              {createInfoItem(
                'Unidades de Negocio',
                `${scoringData.businessUnitCount} unidad${scoringData.businessUnitCount > 1 ? 'es' : ''}`
              )}
              {createInfoItem(
                'Clientes Totales',
                `${scoringData.clients.length} cliente${scoringData.clients.length > 1 ? 's' : ''}`
              )}
            </Grid>
          </Box>
        </Grid>

        {/* Resumen de Business Units por tipo */}
        <Grid size={12}>
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: 'primary.main',
            }}
          >
            Unidades de Negocio
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {(() => {
              const countsByType = scoringData.businessUnits.reduce(
                (acc, bu) => {
                  const type = bu.type || 'default';
                  acc[type] = (acc[type] || 0) + 1;
                  return acc;
                },
                {} as Record<string, number>
              );

              const typeOrder: Array<{ type: string; label: string }> = [
                { type: 'financial_institution', label: 'Instituciones Financieras' },
                { type: 'car_dealership', label: 'Concesionarios' },
                { type: 'insurance_broker', label: 'Corredor de Seguros' },
                { type: 'default', label: 'Por defecto' },
              ];

              return typeOrder
                .filter(({ type }) => countsByType[type] > 0)
                .map(({ type, label }) => (
                  <Box
                    key={type}
                    sx={{
                      p: 2,
                      borderRadius: RADIUS_ITEM,
                      bgcolor: SURFACE_CONTAINER_LOWEST,
                      boxShadow: SHADOW,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                      }}
                    >
                      {label}
                    </Typography>
                    <Chip label={countsByType[type]} size="small" color="primary" />
                  </Box>
                ));
            })()}
          </Box>
        </Grid>

        {/* Lista de Clientes */}
        <Grid size={12}>
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              mt: 2,
            }}
          >
            Clientes Asociados
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {scoringData.clients.map((client) => (
              <Box
                key={client.id}
                onClick={() => window.open(`/clients/${client.id}`, '_blank')}
                sx={{
                  p: 2,
                  borderRadius: RADIUS_ITEM,
                  bgcolor: SURFACE_CONTAINER_LOWEST,
                  boxShadow: SHADOW,
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(21, 24, 58, 0.12)',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body1"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {`${client.firstName || ''} ${client.lastName || ''}`.trim() || `Cliente #${client.id}`}
                    </Typography>
                    {client.email && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          mb: 0.5,
                        }}
                      >
                        {client.email}
                      </Typography>
                    )}
                    {(client.rut || client.dni) && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          mb: 0.5,
                        }}
                      >
                        {client.rut || client.dni}
                      </Typography>
                    )}
                    {client.businessUnit && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          mb: 0.5,
                        }}
                      >
                        {client.businessUnit.name} (
                        {businessUnitTypeLabels[client.businessUnit.type] || client.businessUnit.type})
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      Creado: {new Date(client.createdAt).toLocaleDateString('es-CL')}
                    </Typography>
                  </Box>
                  <Chip label={`ID: ${client.id}`} size="small" variant="outlined" sx={{ ml: 2 }} />
                </Box>
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export const renderScoring = (client: Client) => <ScoringSection clientId={client.id} />;

export const renderBankOffers = (client: Client) => <BankOffersSection clientId={client.id} />;
